import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';

import {
  ApprovalActionsCell,
  ApprovalAggregateApprovedHours,
  ApprovalAggregateHours,
  ApprovalDescriptionCell,
  ApprovalNameCell,
  ApprovalProgressCell,
  ApprovalSelectCell,
  ApprovalStatusCell,
  ApprovalTimeOffsCell,
  asMemberRow,
  asTimeEntry,
  asTimeOffEntry,
  EditableApprovalNotesCell,
  EditableApprovedHoursCell,
  isLoadMoreTableRow,
  isMemberRow,
  isTimeEntryRow,
  isTimeOffRow,
} from '../components/approvals-table/cells';
import { EmptyDescriptionScope } from '../components/modals/empty-description-info-modal';
import { formatApprovalMoney } from '../helpers/format-approval-money.helper';
import { formatHours, formatTimeEntryDuration } from '../helpers/format-time-track.helper';
import { getApprovalsTableMeta } from '../types/approvals-table.types';
import { ApprovalProjectRow, ApprovalTimeEntry } from '../types/time-tracking-approvals';

const columnHelper = createColumnHelper<ApprovalProjectRow>();

const t = (key: string) => i18n.t(`authenticated.timeTracking:approvals.columns.${key}`);

// eslint-disable-next-line i18next/no-literal-string
const EmDash = () => <span className="text-muted-foreground">—</span>;

const MoneyValue = ({
  amount,
  currencyCode,
  emphasized = false,
}: {
  amount: number | null;
  currencyCode: string | null;
  emphasized?: boolean;
}) => {
  if (amount === null) {
    return <EmDash />;
  }
  return (
    <span className={emphasized ? 'whitespace-nowrap font-medium' : 'whitespace-nowrap'}>
      {formatApprovalMoney(amount, currencyCode)}
    </span>
  );
};

export interface GetApprovalsTableColumnsOptions {
  isApproveActionsVisible: boolean;
  /** Gates the External rate / Approved income columns (ProjectsBillingManage). */
  isBillingRatesVisible: boolean;
  onApprove: (entry: ApprovalTimeEntry) => void;
  onRevert: (entry: ApprovalTimeEntry) => void;
  onShowEmptyDescriptionInfo: (scope: EmptyDescriptionScope) => void;
}

/**
 * Column definitions for the approvals tree table. Each cell delegates to a
 * dedicated component in `../components/approvals-table/cells`; this file only
 * wires rows to those cells and declares sizing.
 */
export const getApprovalsTableColumns = ({
  isApproveActionsVisible,
  isBillingRatesVisible,
  onApprove,
  onRevert,
  onShowEmptyDescriptionInfo,
}: GetApprovalsTableColumnsOptions): ColumnDef<ApprovalProjectRow>[] => [
  columnHelper.display({
    id: 'select',
    // No select-all header checkbox: the projects list is cursor-paginated, so a
    // header toggle could only ever cover the loaded page — misleading. Rows are
    // selected per project/member/entry instead.
    header: () => <div className="w-5" />,
    cell: ({ row }) => (isLoadMoreTableRow(row) ? null : <ApprovalSelectCell row={row} />),
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    size: 40,
    minSize: 40,
    maxSize: 40,
  }),

  columnHelper.accessor('project.projectName', {
    id: 'projectMember',
    header: t('projectMember'),
    cell: ({ row, table }) => (
      <ApprovalNameCell
        row={row}
        meta={getApprovalsTableMeta(table)}
        onShowEmptyDescriptionInfo={onShowEmptyDescriptionInfo}
        isExpanded={row.getIsExpanded()}
        canExpand={row.getCanExpand()}
      />
    ),
    enableHiding: false,
    size: 280,
    minSize: 180,
    maxSize: 400,
    meta: {
      cellClassName: 'group-hover:no-underline',
    },
  }),

  columnHelper.accessor('trackedHours', {
    header: t('tracked'),
    cell: ({ row }) => {
      if (isLoadMoreTableRow(row)) {
        return null;
      }
      if (isTimeOffRow(row)) {
        // Informational marker next to the date — time-off rows track nothing.
        return <Badge variant="timeOff">{t('timeOffBadge')}</Badge>;
      }
      if (isTimeEntryRow(row)) {
        return (
          <span className="text-sm">{formatTimeEntryDuration(asTimeEntry(row).trackedHours)}</span>
        );
      }
      if (isMemberRow(row)) {
        return <ApprovalAggregateHours hours={asMemberRow(row).trackedHours} />;
      }
      return <ApprovalAggregateHours hours={row.original.trackedHours} emphasized />;
    },
    size: 100,
  }),

  columnHelper.accessor('bookedHours', {
    header: t('booked'),
    cell: ({ row }) => {
      if (isLoadMoreTableRow(row) || isTimeEntryRow(row)) {
        return null;
      }
      if (isMemberRow(row)) {
        return <ApprovalAggregateHours hours={asMemberRow(row).bookedHours} />;
      }
      return <ApprovalAggregateHours hours={row.original.bookedHours} emphasized />;
    },
    size: 100,
  }),

  columnHelper.accessor('timeOffHours', {
    id: 'timeOffs',
    header: t('timeOffs'),
    cell: ({ row }) => {
      if (isLoadMoreTableRow(row)) {
        return null;
      }
      if (isTimeOffRow(row)) {
        // Leaf time-off rows show their own duration, suppressing "0m" (e.g. "8h" not
        // "8h 0m") — unlike worklog entries, which always show minute precision.
        return <span className="text-sm">{formatHours(asTimeOffEntry(row).durationHours)}</span>;
      }
      if (isTimeEntryRow(row)) {
        return null;
      }
      const rowData = isMemberRow(row) ? asMemberRow(row) : row.original;
      return (
        <ApprovalTimeOffsCell
          timeOffHours={rowData.timeOffHours}
          approvedTimeOffHours={rowData.approvedTimeOffHours}
          pendingTimeOffHours={rowData.pendingTimeOffHours}
          emphasized={!isMemberRow(row)}
        />
      );
    },
    size: 100,
  }),

  ...(isBillingRatesVisible
    ? [
        columnHelper.accessor('externalRate', {
          id: 'externalRate',
          header: () => <span className="whitespace-nowrap">{t('externalRate')}</span>,
          cell: ({ row }) => {
            if (isLoadMoreTableRow(row) || isTimeOffRow(row)) {
              return null;
            }
            if (isTimeEntryRow(row)) {
              const entry = asTimeEntry(row);
              return <MoneyValue amount={entry.externalRate} currencyCode={entry.currencyCode} />;
            }
            const rowData = isMemberRow(row) ? asMemberRow(row) : row.original;
            return (
              <MoneyValue
                amount={rowData.externalRate}
                currencyCode={rowData.currencyCode}
                emphasized={!isMemberRow(row)}
              />
            );
          },
          size: 120,
          minSize: 100,
          meta: { title: t('externalRate') },
        }),
      ]
    : []),

  columnHelper.display({
    id: 'description',
    header: t('description'),
    cell: ({ row }) =>
      isLoadMoreTableRow(row) ? null : (
        <ApprovalDescriptionCell
          row={row}
          onShowEmptyDescriptionInfo={onShowEmptyDescriptionInfo}
        />
      ),
    size: 200,
  }),

  // One column, two stacked values: approved hours are split by purpose (what the client is
  // invoiced for vs what the member is paid for), but a second column would cost far more
  // horizontal space than two compact rows cost vertically.
  columnHelper.accessor('approvedForInvoiceHours', {
    // Column ids are persisted per user (column order in localStorage, keyed by id — see
    // `useColumnOrder`), so this keeps the pre-split id even though the accessor moved to the
    // invoice value. A new id is absent from every saved order, and TanStack appends unknown
    // columns last — the Approved column would silently jump to the far right of the table.
    id: 'approvedHours',
    header: () => (
      <SimpleTooltip content={t('approvedTooltip')}>
        <span className="whitespace-nowrap">{t('approved')}</span>
      </SimpleTooltip>
    ),
    cell: ({ row, table }) => {
      if (isLoadMoreTableRow(row) || isTimeOffRow(row)) {
        // Time-off rows are informational — nothing to approve.
        return null;
      }
      if (isTimeEntryRow(row)) {
        return (
          <EditableApprovedHoursCell entry={asTimeEntry(row)} meta={getApprovalsTableMeta(table)} />
        );
      }
      if (isMemberRow(row)) {
        const member = asMemberRow(row);
        return (
          <ApprovalAggregateApprovedHours
            approvedForInvoiceHours={member.approvedForInvoiceHours}
            approvedForBillHours={member.approvedForBillHours}
          />
        );
      }
      return (
        <ApprovalAggregateApprovedHours
          approvedForInvoiceHours={row.original.approvedForInvoiceHours}
          approvedForBillHours={row.original.approvedForBillHours}
          emphasized
        />
      );
    },
    enableHiding: false,
    size: 150,
    minSize: 130,
    meta: { title: t('approved') },
  }),

  columnHelper.display({
    id: 'approvalNotes',
    header: t('approvalNotes'),
    cell: ({ row, table }) => {
      if (isLoadMoreTableRow(row) || isTimeOffRow(row)) {
        return null;
      }
      if (isTimeEntryRow(row)) {
        return (
          <EditableApprovalNotesCell entry={asTimeEntry(row)} meta={getApprovalsTableMeta(table)} />
        );
      }
      return <EmDash />;
    },
    size: 220,
    minSize: 160,
  }),

  columnHelper.display({
    id: 'status',
    header: t('status'),
    cell: ({ row }) => (isLoadMoreTableRow(row) ? null : <ApprovalStatusCell row={row} />),
    size: 120,
  }),

  ...(isBillingRatesVisible
    ? [
        columnHelper.accessor('approvedIncome', {
          id: 'approvedIncome',
          header: () => <span className="whitespace-nowrap">{t('approvedIncome')}</span>,
          cell: ({ row }) => {
            if (isLoadMoreTableRow(row) || isTimeOffRow(row)) {
              return null;
            }
            if (isTimeEntryRow(row)) {
              const entry = asTimeEntry(row);
              return <MoneyValue amount={entry.approvedIncome} currencyCode={entry.currencyCode} />;
            }
            const rowData = isMemberRow(row) ? asMemberRow(row) : row.original;
            return (
              <MoneyValue
                amount={rowData.approvedIncome}
                currencyCode={rowData.currencyCode}
                emphasized={!isMemberRow(row)}
              />
            );
          },
          size: 140,
          minSize: 120,
          meta: { title: t('approvedIncome') },
        }),
      ]
    : []),

  columnHelper.accessor('approvalPercentage', {
    header: () => <span className="whitespace-nowrap">{t('approvalPercentage')}</span>,
    cell: ({ row }) => {
      if (isLoadMoreTableRow(row) || isTimeOffRow(row)) {
        // Approve/decline actions are hidden for informational time-off rows.
        return null;
      }
      if (isTimeEntryRow(row)) {
        return (
          <ApprovalActionsCell
            entry={asTimeEntry(row)}
            isApproveActionsVisible={isApproveActionsVisible}
            onApprove={onApprove}
            onRevert={onRevert}
          />
        );
      }
      if (isMemberRow(row)) {
        const member = asMemberRow(row);
        return (
          <ApprovalProgressCell
            percentage={member.approvalPercentage}
            approvedForInvoiceHours={member.approvedForInvoiceHours}
            bookedHours={member.bookedHours}
          />
        );
      }
      return (
        <ApprovalProgressCell
          percentage={row.original.approvalPercentage}
          approvedForInvoiceHours={row.original.approvedForInvoiceHours}
          bookedHours={row.original.bookedHours}
        />
      );
    },
    size: 180,
    minSize: 170,
    // Always the last column (it carries the approve/decline actions) — keep it fixed in the
    // column selector so users can't reorder it, without pinning/freezing it in the table.
    meta: { title: t('approvalPercentage'), fixedInSelector: true },
  }),
];
